import React, { useState } from 'react';
import { Share, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import '../i18n';
import { usePwaInstall, PLAY_STORE_URL } from '../lib/usePwaInstall';
import { useAuth } from '../context/AuthContext';

/* Dismissible mobile banner offering to install the app: the Play Store
   listing on Android, or PWA-install steps on iOS (which has no app store
   listing). Dismissal (or viewing the iOS steps) snoozes it for 30 days via
   usePwaInstall. */
function InstallBanner() {
  const { t } = useTranslation();
  const { mode, dismiss } = usePwaInstall();
  const [showSteps, setShowSteps] = useState(false);
  const { isAuthenticated } = useAuth();

  if (!mode) return null;

  const closeSteps = () => {
    setShowSteps(false);
    dismiss();
  };

  // BottomNav (see App.js) is fixed to the bottom on mobile whenever the user is
  // signed in; sit above it there instead of flush at the bottom. 4rem+1px matches
  // BottomNav's explicit h-16 plus its border-t exactly (see BottomNav.js) — update
  // both together if either changes.
  const bottomOffset = isAuthenticated
    ? 'bottom-[calc(4rem+1px+env(safe-area-inset-bottom))] md:bottom-2'
    : 'bottom-2';

  return (
    <>
      <div className={`fixed inset-x-2 ${bottomOffset} z-50 flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-xl md:hidden`}>
        <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-foreground">Laftel Lingo</p>
          <p className="text-sm text-muted-foreground">
            {t(mode === 'android' ? 'pwa.androidBannerSubtitle' : 'pwa.bannerSubtitle')}
          </p>
        </div>
        {mode === 'android' ? (
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            onClick={dismiss}
          >
            {t('pwa.getApp')}
          </a>
        ) : (
          <button
            type="button"
            className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            onClick={() => setShowSteps(true)}
          >
            {t('pwa.showMeHow')}
          </button>
        )}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground p-1 rounded"
          aria-label={t('pwa.dismiss')}
          onClick={dismiss}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {showSteps && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={closeSteps}
        >
          <div
            className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">{t('pwa.installTitle', { app: 'Laftel Lingo' })}</h3>
              <button
                onClick={closeSteps}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                aria-label={t('pwa.close')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <ol className="space-y-3">
                <li className="flex items-start gap-2">
                  <Share className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{t('pwa.iosStep1')}</span>
                </li>
                <li>{t('pwa.iosStep2')}</li>
                <li>{t('pwa.iosStep3')}</li>
              </ol>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end">
              <button
                type="button"
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                onClick={closeSteps}
              >
                {t('pwa.done')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallBanner;
